// ABOUTME: Step 2 of license purchase (DVLP-7860). The right-hand form is the course
// ABOUTME: checkout's own PaymentCheckoutForm, unchanged: one Stripe integration, not two.
import { useMemo } from 'react';
import { observer } from 'mobx-react';
import { just } from 'maybeasy';
import { PaymentIntent } from '@stripe/stripe-js';
import { Button, Typography } from '@execonline-inc/execonline-ui';
import { NotTranslated, T } from '../../../../../Translations';
import {
  PaymentCheckoutFormReactions,
  PaymentCheckoutFormStore,
  PaymentProcessing,
} from '../../ProgramExperienceEnrollment/ExperiencePaymentContent/Common';
import PaymentCheckoutForm from '../../ProgramExperienceEnrollment/ExperiencePaymentContent/PaymentCheckoutModalBody/PaymentCheckoutForm';
import licensePurchaseStore from '../Store';
import { Paying, StartingPurchase } from '../Types';
import { Price, TermLabel, WizardSteps } from './Common';

interface PanelProps {
  flow: Paying | StartingPurchase;
}

/** Same panel, proportions and color as the course checkout's PaymentOrderDetails. */
const OrderPanel = observer(function OrderPanel({ flow }: PanelProps) {
  const { offer } = flow;
  return (
    <div
      data-intercom-target="license-pay-order-panel"
      className="flex min-h-0 flex-col overflow-y-auto rounded-md bg-[#F7F7F7] px-6 pb-6 pt-8 md:basis-2/5 md:pl-8 md:pr-6"
    >
      <WizardSteps active={2} onChoose={licensePurchaseStore.backToChoosing} />

      <Typography
        as="p"
        variant="caption"
        fontWeight="bold"
        className="uppercase tracking-widest text-gray-500"
      >
        <T kind="ExecOnline" />
      </Typography>
      <Typography
        as="h2"
        variant="subtitleLarge"
        fontWeight="bold"
        className="mt-1 tracking-normal"
      >
        <T kind="Order Details" />
      </Typography>

      <div className="mt-5 bg-white p-4 shadow-[0_0_12px_0_rgba(0,0,0,0.12)]">
        <Typography as="p" variant="bodyMedium" fontWeight="semibold">
          <NotTranslated text={offer.name} />
        </Typography>
        <Typography as="p" variant="caption" color="gray" className="mt-1">
          <T kind="1 seat" />
          {' · '}
          <TermLabel offer={offer} />
        </Typography>
        <Button
          data-intercom-target="license-pay-change-license-button"
          className="mt-2.5 p-0 font-normal"
          variant="link"
          size="sm"
          onClick={licensePurchaseStore.backToChoosing}
        >
          <T kind="Change license" />
        </Button>
      </div>

      <div className="mt-8 flex flex-col gap-y-4">
        <div className="flex justify-between">
          <Typography className="leading-normal">
            <T kind="Subtotal" />
          </Typography>
          <Typography className="leading-normal">
            <Price cents={offer.priceInCents} />
          </Typography>
        </div>
        <div className="flex justify-between">
          <Typography className="leading-normal">
            <T kind="Tax" />
          </Typography>
          <Typography className="leading-normal">
            <T kind="Included" />
          </Typography>
        </div>
        <div className="my-1.5 flex w-full border-b border-gray-300" />
        <div className="flex justify-between">
          <Typography className="leading-normal">
            <T kind="Total" />
          </Typography>
          <Typography className="leading-normal" fontWeight="bold">
            <Price cents={offer.priceInCents} />
          </Typography>
        </div>
      </div>

      <div className="mt-auto pt-6">
        <Button
          data-intercom-target="license-pay-back-button"
          color="gray"
          variant="text"
          size="sm"
          onClick={licensePurchaseStore.backToChoosing}
        >
          ‹ <T kind="Back to licenses" />
        </Button>
      </div>
    </div>
  );
});

const onSuccess = (intent: PaymentIntent) => {
  licensePurchaseStore.paymentConfirmed(intent.id);
};

const ReadyToPay = observer(function ReadyToPay({ flow }: { flow: Paying }) {
  // Keyed on the intent by the parent, so each PaymentIntent gets a fresh form store.
  const store = useMemo(() => new PaymentCheckoutFormStore(), []);
  return (
    // Fills the fixed-height dialog; the form inside scrolls its fields and pins its button.
    <div
      data-intercom-target="license-pay-payment-panel"
      className="flex min-h-0 flex-col overflow-hidden md:basis-3/5"
    >
      {/* Level with the dialog's close button, with room for it on the right, so the
          fields start below it rather than under it. */}
      <Typography
        as="h2"
        variant="subtitleLarge"
        fontWeight="bold"
        className="flex-none px-6 pr-16 pt-8 tracking-normal md:px-8 md:pr-16"
      >
        <T kind="Payment details" />
      </Typography>
      <PaymentCheckoutForm
        price={just(flow.offer.priceInCents)}
        store={store}
        stripe={flow.stripe}
        paymentIntentResource={just(flow.paymentIntentResource)}
        checkout="license"
      />
      <PaymentCheckoutFormReactions store={store} onSuccess={onSuccess} />
    </div>
  );
});

interface Props {
  flow: Paying | StartingPurchase;
}

function PayForLicense({ flow }: Props) {
  return (
    <>
      <OrderPanel flow={flow} />
      {flow.kind === 'paying' ? (
        <ReadyToPay key={flow.paymentIntentResource.payload.id} flow={flow} />
      ) : (
        <div
          data-intercom-target="license-pay-loading"
          className="flex flex-col items-center justify-center py-24 md:basis-3/5 md:py-0"
        >
          <PaymentProcessing />
        </div>
      )}
    </>
  );
}

export default observer(PayForLicense);
